import type {
  GuardianNotificationApiType,
  GuardianNotificationDestination,
} from '@/features/guardian/notifications/types';
import { warnLog } from '@/libs/logger';
import { navigationRef } from '@/navigations/navigation-ref';
import { useSessionStore } from '@/stores/session-store';

export type NotificationOpenSource = 'tap' | 'initial';

export type GuardianNotificationPayload = {
  notificationId?: string;
  type: GuardianNotificationApiType;
  destination?: GuardianNotificationDestination;
  playerId?: string;
  sessionId?: string;
  bookingId?: string;
  invitationToken?: string;
};

type RouteOptions = { markAsRead?: boolean };
type PendingNotification = {
  payload: GuardianNotificationPayload;
  source: NotificationOpenSource;
  options: RouteOptions;
};

const KEEP_CURRENT_OR_HOME_TYPES = new Set<GuardianNotificationApiType>([
  'guardian_removed',
  'invitation_expired_coguardian',
]);

let pendingNotification: PendingNotification | null = null;
let lastHandledNotification: { id: string; at: number } | null = null;
const DUPLICATE_TAP_WINDOW_MS = 1_500;

/**
 * Single entry point for system-notification taps and Notification Center item presses.
 * Runtime payloads are validated here so malformed/old pushes never crash navigation.
 */
export function routeNotification(
  data?: Record<string, string | number | object> | GuardianNotificationPayload | null,
  source: NotificationOpenSource = 'tap',
  options: RouteOptions = {},
): void {
  const payload = parsePayload(data);
  if (!payload) return;

  if (
    payload.notificationId &&
    payload.notificationId === lastHandledNotification?.id &&
    Date.now() - lastHandledNotification.at < DUPLICATE_TAP_WINDOW_MS
  ) {
    return;
  }

  const session = useSessionStore.getState();
  const isPublicInvitation =
    payload.destination === 'invitation_response' || payload.type === 'invite_received';
  const canNavigate =
    navigationRef.isReady() &&
    session.isInitialized &&
    (isPublicInvitation ||
      (session.isAuthenticated && !session.isSoftLoggedOut && session.activeRole === 'guardian'));

  if (!canNavigate) {
    pendingNotification = { payload, source, options };
    return;
  }

  pendingNotification = null;
  lastHandledNotification = payload.notificationId
    ? { id: payload.notificationId, at: Date.now() }
    : null;
  if (options.markAsRead !== false && payload.notificationId && session.isAuthenticated) {
    markAsRead(payload.notificationId, session.user?.uid).catch(error =>
      warnLog('[push] failed to mark notification as read', error),
    );
  }

  navigateNotification(payload, source, session.user?.uid).catch(error =>
    warnLog('[push] failed to route notification', { payload, error }),
  );
}

/** Retry a notification captured before auth/session/navigation was ready. */
export function consumePendingNotification(): void {
  if (!pendingNotification) return;
  const { payload, source, options } = pendingNotification;
  routeNotification(payload, source, options);
}

/**
 * Warm lazy notification dependencies only after the React tree has mounted. Keeping these out
 * of module scope avoids the native background-handler bootstrap cycle, while warming them here
 * prevents the first notification tap from waiting for Metro to compile the feature graph in dev.
 */
export function preloadNotificationRoutingDependencies(): void {
  Promise.all([
    import('@/features/guardian/queries/query-keys'),
    import('@/features/guardian/services/get-guardian-players'),
    import('@/libs/tanstack-query/query-client'),
    import('@/features/guardian-player/queries/query-keys'),
    import('@/features/guardian-player/services/player-sessions'),
    import('@/features/auth/services'),
    import('@/queries/use-player-guardians-query'),
    import('@/services/get-player-guardians'),
    import('@/features/guardian/services/notifications'),
  ]).catch(error => warnLog('[push] failed to preload notification dependencies', error));
}

function parsePayload(
  data?: Record<string, string | number | object> | GuardianNotificationPayload | null,
): GuardianNotificationPayload | null {
  if (!data || typeof data.type !== 'string') {
    warnLog('[push] notification payload is missing `type`', data);
    return null;
  }

  const record = data as Record<string, unknown>;

  return {
    type: data.type as GuardianNotificationApiType,
    destination: readDestination(data.destination),
    notificationId: readString(data.notificationId) ?? readString(record.id),
    playerId: readString(data.playerId) ?? readString(record.player_id),
    sessionId: readString(data.sessionId) ?? readString(record.session_id),
    bookingId: readString(data.bookingId) ?? readString(record.booking_id),
    invitationToken:
      readString(data.invitationToken) ??
      readString(record.invitation_token) ??
      readString(record.invitationCode) ??
      readString(record.token),
  };
}

async function navigateNotification(
  payload: GuardianNotificationPayload,
  source: NotificationOpenSource,
  guardianId?: string,
): Promise<void> {
  const destination = payload.destination ?? legacyDestination(payload.type);

  if (KEEP_CURRENT_OR_HOME_TYPES.has(payload.type)) {
    // A live/background tap must not disrupt the current screen. A terminated-app tap
    // intentionally lands on the Guardian homepage after session restoration.
    if (source === 'initial') navigateGuardian('GuardianDashboard');
    return;
  }

  if (destination === 'invitation_response') {
    if (!payload.invitationToken) {
      warnLog('[push] invite_received notification is missing invitation token', payload);
      return;
    }
    navigateInvitation(payload.invitationToken);
    return;
  }

  if (destination === 'booking_details') {
    // The temporary DefaultScreen does not consume booking data. Keep navigating while BE rolls
    // out bookingId; make it required again when the real Booking Details screen is implemented.
    navigateGuardian('GuardianBookingDetails', {
      bookingId: payload.bookingId,
      playerId: payload.playerId,
    });
    return;
  }

  if (!payload.playerId || !guardianId) return fallback(payload, 'playerId');

  try {
    // Keep feature/API modules out of the module-scope dependency graph. This router is loaded
    // by index.js before React mounts for background events; eager imports here create a cycle
    // through AuthServices -> stores -> UI hooks during native bootstrap.
    const [{ guardianKeys }, { fetchGuardianPlayers }, { queryClient }] = await Promise.all([
      import('@/features/guardian/queries/query-keys'),
      import('@/features/guardian/services/get-guardian-players'),
      import('@/libs/tanstack-query/query-client'),
    ]);
    const players = await queryClient.ensureQueryData({
      queryKey: guardianKeys.players(guardianId),
      queryFn: fetchGuardianPlayers,
    });
    const player = players.find(item => item.id === payload.playerId);
    if (!player) return fallback(payload, 'accessible player');

    if (destination === 'session_detail') {
      if (!payload.sessionId) return fallback(payload, 'sessionId');
      navigateGuardian('PlayerSessionDetail', { player, sessionId: payload.sessionId });
      refreshSessionDetail(queryClient, payload.playerId, payload.sessionId).catch(error =>
        warnLog('[push] failed to refresh session detail', error),
      );
      return;
    }

    if (destination === 'kit_and_notes') {
      navigateGuardian('PlayerKitNotes', { player });
      refreshPlayerDetail(queryClient, payload.playerId).catch(error =>
        warnLog('[push] failed to refresh KIT & Notes', error),
      );
      return;
    }

    if (destination === 'player_profile') {
      navigateGuardian('PlayerDetail', { player });
      refreshPlayerDetail(queryClient, payload.playerId, true).catch(error =>
        warnLog('[push] failed to refresh player profile', error),
      );
      return;
    }

    if (destination === 'guardian_circle') {
      navigateGuardian('GuardianCircle', { player });
      refreshGuardianCircle(queryClient, payload.playerId).catch(error =>
        warnLog('[push] failed to refresh guardian circle', error),
      );
      return;
    }

    fallback(payload, 'supported destination');
  } catch (error) {
    warnLog('[push] destination is no longer available', { payload, error });
    navigateGuardian('GuardianNotifications');
  }
}

async function refreshSessionDetail(
  queryClient: import('@tanstack/react-query').QueryClient,
  playerId: string,
  sessionId: string,
): Promise<void> {
  const [{ guardianPlayerKeys }, { fetchGuardianPlayerSessionDetail }] = await Promise.all([
    import('@/features/guardian-player/queries/query-keys'),
    import('@/features/guardian-player/services/player-sessions'),
  ]);
  await queryClient.fetchQuery({
    queryKey: guardianPlayerKeys.sessionDetail(playerId, sessionId),
    queryFn: () => fetchGuardianPlayerSessionDetail(playerId, sessionId),
    staleTime: 0,
  });
}

async function refreshGuardianCircle(
  queryClient: import('@tanstack/react-query').QueryClient,
  playerId: string,
): Promise<void> {
  const [{ playerGuardiansKeys }, { fetchPlayerGuardians }] = await Promise.all([
    import('@/queries/use-player-guardians-query'),
    import('@/services/get-player-guardians'),
  ]);
  await queryClient.fetchQuery({
    queryKey: playerGuardiansKeys.list(playerId),
    queryFn: () => fetchPlayerGuardians(playerId),
    staleTime: 0,
  });
}

async function refreshPlayerDetail(
  queryClient: import('@tanstack/react-query').QueryClient,
  playerId: string,
  includeSessionSummary = false,
): Promise<void> {
  const [{ AuthServices }, { guardianPlayerKeys }, playerSessionServices] = await Promise.all([
    import('@/features/auth/services'),
    import('@/features/guardian-player/queries/query-keys'),
    import('@/features/guardian-player/services/player-sessions'),
  ]);

  const requests: Promise<unknown>[] = [
    queryClient.fetchQuery({
      queryKey: guardianPlayerKeys.playerDetail(playerId),
      queryFn: () => AuthServices.getGuardianPlayerDetail(playerId),
      staleTime: 0,
    }),
  ];

  if (includeSessionSummary) {
    requests.push(
      queryClient.fetchQuery({
        queryKey: guardianPlayerKeys.sessionSummary(playerId),
        queryFn: () => playerSessionServices.fetchGuardianPlayerSessionSummary(playerId),
        staleTime: 0,
      }),
    );
  }

  await Promise.all(requests);
}

async function markAsRead(notificationId: string, guardianId?: string): Promise<void> {
  const [{ guardianKeys }, { markGuardianNotificationRead }, { queryClient }] = await Promise.all([
    import('@/features/guardian/queries/query-keys'),
    import('@/features/guardian/services/notifications'),
    import('@/libs/tanstack-query/query-client'),
  ]);
  await markGuardianNotificationRead(notificationId);
  await queryClient.invalidateQueries({ queryKey: guardianKeys.notifications(guardianId) });
}

function navigateGuardian(
  screen: 'GuardianDashboard' | 'GuardianNotifications',
  params?: undefined,
): void;
function navigateGuardian(
  screen: 'GuardianBookingDetails',
  params: { bookingId?: string; playerId?: string },
): void;
function navigateGuardian(
  screen: 'PlayerDetail' | 'PlayerKitNotes' | 'GuardianCircle',
  params: object,
): void;
function navigateGuardian(screen: 'PlayerSessionDetail', params: object): void;
function navigateGuardian(screen: string, params?: object): void {
  if (!navigationRef.isReady()) return;
  navigationRef.navigate('Guardian', { screen, params } as never);
}

function navigateInvitation(token: string): void {
  if (!navigationRef.isReady()) return;
  navigationRef.navigate('GuardianInvitation', { token });
}

function fallback(payload: GuardianNotificationPayload, missing: string): void {
  warnLog(`[push] notification is missing ${missing}`, payload);
  navigateGuardian('GuardianNotifications');
}

function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function readDestination(value: unknown): GuardianNotificationDestination | undefined {
  switch (value) {
    case 'booking_details':
    case 'session_detail':
    case 'kit_and_notes':
    case 'player_profile':
    case 'guardian_circle':
    case 'invitation_response':
      return value;
    default:
      return undefined;
  }
}

/** Compatibility for older FCM payloads sent before BE added `destination`. */
function legacyDestination(
  type: GuardianNotificationApiType,
): GuardianNotificationDestination | undefined {
  switch (type) {
    case 'class_start_reminder':
    case 'class_cancelled_coach':
    case 'class_rescheduled_coach':
    case 'class_booked':
    case 'class_cancelled_guardian':
    case 'class_rescheduled_guardian':
      return 'booking_details';
    case 'pin_earned':
    case 'level_completed':
      return 'session_detail';
    case 'coach_note':
      return 'kit_and_notes';
    case 'package_purchased':
    case 'kid_profile_updated':
      return 'player_profile';
    case 'invite_accepted':
    case 'invite_declined':
    case 'invitation_expired':
    case 'guardian_left':
      return 'guardian_circle';
    case 'invite_received':
      return 'invitation_response';
    default:
      return undefined;
  }
}
